import RichText from "@/components/common/richText/richText.component";
import { Progress } from "@/components/ui/progress";
import { cn } from "@/lib/utils";
import PhoneFrame from "../phoneFrame/phoneFrame.component";
import type {
  WhatsAppChatHeader,
  WhatsAppExtraKind,
  WhatsAppMessage,
} from "./whatsAppPhone.type";

interface WhatsAppPhoneProps {
  chat: WhatsAppChatHeader;
  messages: WhatsAppMessage[];
  // Pop each bubble in one after another (hero / live chats).
  isAnimated?: boolean;
}

// The coloured block under a message (the preview's `.wbx-*`).
const extraClass: Record<WhatsAppExtraKind, string> = {
  wallet: "bg-brand-gradient text-white",
  ticket: "bg-tone-lilac-ink text-white",
  code: "bg-tone-mint-ink text-white",
  banner: "bg-primary text-white",
  stars: "bg-tone-sun text-center text-xl tracking-[4px] text-tone-sun-ink",
  progress: "bg-paper text-ink",
};

// Staggered pop-in delays for the animated bubbles.
const popDelay = ["delay-300", "delay-1100", "delay-1900", "delay-2700"];

// The ONE WhatsApp chat mock-up for every 2.0 page: the shared iPhone-style
// PhoneFrame with a WhatsApp-green status bar running into the business
// chat header, then message bubbles (incoming white on the left, outgoing
// green on the right) with timestamps, coloured extras and reply buttons.
const WhatsAppPhone = ({ chat, messages, isAnimated = false }: WhatsAppPhoneProps) => (
  <PhoneFrame statusBar="whatsapp" contentClassName="flex flex-col">
    <div className="flex items-center gap-2.5 bg-tone-mint-ink px-3 pt-1 pb-2.5 text-white">
      <span
        aria-hidden="true"
        className="grid size-[34px] shrink-0 place-items-center rounded-full bg-white font-extrabold text-tone-mint-ink"
      >
        {chat.initial}
      </span>
      <div className="min-w-0">
        <b className="block truncate text-sm">{chat.shop}</b>
        <small className="block text-[11.5px] text-white/85">{chat.subtitle}</small>
      </div>
    </div>
    <div className="grid flex-1 content-start gap-2 bg-cream px-2.5 pt-3.5 pb-9 text-ink">
      {messages.map((message, index) => (
        <div
          key={`${message.time}-${index}`}
          className={cn(
            "relative grid max-w-[88%] gap-1.5 rounded-xl px-2.5 pt-2 pb-[18px] text-[13px] leading-[1.4] shadow-sm",
            message.isOutgoing
              ? "justify-self-end rounded-tr-[2px] bg-tone-mint"
              : "justify-self-start rounded-tl-[2px] bg-white",
            isAnimated &&
              cn(
                "motion-safe:animate-in motion-safe:fill-mode-both motion-safe:fade-in motion-safe:zoom-in-50 motion-safe:animation-duration-500",
                message.isOutgoing ? "origin-bottom-right" : "origin-bottom-left",
                popDelay[index % popDelay.length],
              ),
          )}
        >
          {message.text !== undefined && (
            <span className="whitespace-pre-line">
              <RichText text={message.text} />
            </span>
          )}
          {message.content}
          {message.extra && (
            <span
              className={cn(
                "mt-1 grid gap-0.5 rounded-[10px] px-3 py-2.5",
                extraClass[message.extra.kind],
              )}
            >
              {message.extra.label && (
                <span className="text-[11.5px] opacity-90">{message.extra.label}</span>
              )}
              {message.extra.value && (
                <b className="text-xl">{message.extra.value}</b>
              )}
              {message.extra.progress !== undefined && (
                <Progress
                  value={message.extra.progress}
                  aria-label={message.extra.label}
                  className="mt-1 h-2 bg-white"
                  indicatorClassName="bg-primary"
                />
              )}
            </span>
          )}
          {message.button && (
            <span className="mt-1 border-t border-line px-1 pt-1.5 text-center font-bold text-tone-sky-ink">
              {message.button}
            </span>
          )}
          <time className="absolute right-2 bottom-1 text-[10px] text-ink-muted">
            {message.time}
          </time>
        </div>
      ))}
    </div>
  </PhoneFrame>
);

export default WhatsAppPhone;
